import { ActionIcon, Badge, Tooltip } from '@mantine/core';
import { useState } from 'react';
import { IconChevronDown, IconComponents, IconSettings } from '@tabler/icons-react';
import clsx from 'clsx';
import { CollapseUnmount } from '~/components/common/collapse/CollapseUnmount';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import OperationControl from '../../OperationControl';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { ComponentCallOpInner } from './ComponentCallOpInner';
import { ComponentParameters } from './ComponentParameters';
import { ComponentCallOpEdit } from './ComponentCallOpEdit';
import { ComponentReferencesHoverCard } from './ComponentReferencesHoverCard';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { ComponentModificationWarningCard } from '~/components/test/steps/step/operation/complex/component/ComponentModificationWarningCard';
import { componentModificationWarningAtom } from '~/atoms/no-code-atoms';
import { useAtomValue } from 'jotai';

export default function ComponentCallOp({
  operation,
  testContentId,
  step,
  previewCursorAllowed,
  readonly,
  disableMenu,
  parentContexts,
}: {
  operation: SelectedOperation;
  testContentId: string;
  step: SelectedStep;
  previewCursorAllowed: boolean;
  readonly?: boolean;
  disableMenu?: boolean;
  parentContexts?: OperationParentContext[];
}) {
  const parentContext = parentContexts?.at(-1);
  const componentModificationWarningState = useAtomValue(componentModificationWarningAtom);
  const componentId = operation.componentCallOp?.componentId || '';
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isArchived = operation.componentCallOp?.component?.archived;

  const shouldShowComponentModificationWarning =
    // Ensure we're modifying the correct component
    componentModificationWarningState?.componentId === componentId &&
    // Ensure we're modifying the correct callOp; there could be two instances of the same component
    // but the only difference between two component callOps is a different root operation ID
    // We need to handle both scenarios
    // Scenario 1: Modifying nested component callOp (has root operation ID, use that)
    // Scenario 2: Modifying top level component callOp (doesn't have root operation ID, but we're already in the root operation, use operation ID)
    (componentModificationWarningState.rootOperationId === parentContext?.rootOperationId ||
      componentModificationWarningState?.rootOperationId === operation.id);

  return (
    <div className="flex w-full flex-col items-start py-[0.2rem]">
      <div className="w-full flex-1 rounded border border-gray-200 bg-[var(--mantine-color-body)] dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between rounded bg-gray-50 px-2 py-1 dark:bg-neutral-700/20">
          <div className="flex w-full items-center justify-between gap-2">
            <ComponentCallOpEdit
              componentId={componentId}
              testContentId={testContentId}
              readonly={readonly}
              editing={editing}
              onEditingChange={setEditing}
              parentContext={parentContext}
            >
              {/* Component edit popover target */}
              <div
                className="flex items-center gap-2"
                onDoubleClick={() => setEditing(true)}
              >
                <ComponentReferencesHoverCard
                  componentId={componentId}
                  currentTestContentId={testContentId}
                >
                  <Tooltip
                    label="Archived"
                    disabled={!isArchived}
                  >
                    <Badge
                      color={isArchived ? 'gray' : 'blue'}
                      variant="light"
                      radius="sm"
                      className="normal-case shadow-sm"
                      leftSection={<IconComponents size={12} />}
                    >
                      {operation.componentCallOp?.component?.name}
                    </Badge>
                  </Tooltip>
                </ComponentReferencesHoverCard>
              </div>
            </ComponentCallOpEdit>

            {/* Expand/Collapse and operation control */}
            <div className="flex items-center gap-2">
              <ActionIcon
                title={expanded ? 'Collapse' : 'Expand'}
                className="mr-1"
                size="xs"
                variant="transparent"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
              >
                <IconChevronDown
                  className={clsx('transition-all duration-200 ease-in-out', {
                    'rotate-180': expanded,
                  })}
                  size={15}
                />
              </ActionIcon>

              {!disableMenu && (
                <Tooltip
                  label="Archived components cannot be edited"
                  disabled={!isArchived}
                >
                  <ActionIcon
                    size="xs"
                    radius="xl"
                    variant="transparent"
                    color="gray"
                    disabled={isArchived}
                    onClick={() => {
                      setEditing((prev) => !prev);
                    }}
                  >
                    <IconSettings />
                  </ActionIcon>
                </Tooltip>
              )}

              {!disableMenu && (
                <div className={clsx({ invisible: readonly || editing })}>
                  <OperationControl
                    operation={operation}
                    previewCursorAllowed={previewCursorAllowed}
                    testContentId={testContentId}
                    stepOrder={step.stepOrder}
                    setEditing={setEditing}
                    parentContext={parentContext}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Expandable Content - Only Actions */}
        <CollapseUnmount in={expanded}>
          <ComponentParameters
            componentCallOp={operation.componentCallOp}
            testContentId={testContentId}
            operationId={operation.id || ''}
          />
          <div className="w-full border-t border-gray-200 dark:border-gray-700" />
          {/* Warning card when a user modifies a component */}
          {!!shouldShowComponentModificationWarning && <ComponentModificationWarningCard componentId={componentId} />}
          <div className="px-3 py-1">
            <ComponentCallOpInner
              componentId={componentId}
              testContentId={testContentId}
              readonly={readonly}
              step={step}
              parentRootOperationId={operation.id}
              parentOperationOrder={operation.order}
              parentContexts={parentContexts}
            />
          </div>
        </CollapseUnmount>
      </div>
    </div>
  );
}
